/* eslint-disable */
import React, { useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Typography,
  IconButton,
} from "@material-ui/core";
import Button from "components/CustomButtons/Button";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faArrowDown,
  faArrowUp,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { formatNumber } from "utils/formatNumberUtils.js";
import AuthContext from "contexts/AuthProvider.js";
import { parseHexColor, getAlphaColor } from "utils/colorUtils.js";
import { ThreeDots } from "react-loader-spinner";
import PerfectScrollbar from "react-perfect-scrollbar";

// SEU utils (adaptado):
import {
  buildHierarchyFromInitiatives,
  sortDataRecursively,
} from "../../../../utils/hierarchyUtils.js";
import { client } from "services/axiosClient.js";

// =============== CONSTANTS / UTILS =================

const circleSize = 20; // Reduzido de 30 para 20

function getStatusStyles(status, isHovered) {
  let baseStyle = {};
  switch (status) {
    case "green":
      baseStyle = {
        background: "linear-gradient(135deg, #66bb6a 0%, #43a047 100%)",
        boxShadow: isHovered ? "0 0 8px rgba(67,160,71,0.5)" : "0 0 4px rgba(67,160,71,0.3)",
      };
      break;
    case "yellow":
      baseStyle = {
        background: "linear-gradient(135deg, #ffd54f 0%, #ffc107 100%)",
        boxShadow: isHovered ? "0 0 8px rgba(255,193,7,0.5)" : "0 0 4px rgba(255,193,7,0.3)",
      };
      break;
    case "red":
      baseStyle = {
        background: "linear-gradient(135deg, #ef5350 0%, #e53935 100%)",
        boxShadow: isHovered ? "0 0 8px rgba(229,57,53,0.5)" : "0 0 4px rgba(229,57,53,0.3)",
      };
      break;
    default:
      baseStyle = {
        background: "linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)",
        boxShadow: isHovered ? "0 0 8px rgba(158,158,158,0.5)" : "0 0 4px rgba(158,158,158,0.3)",
      };
  }
  return {
    width: circleSize,
    height: circleSize,
    borderRadius: "50%",
    display: "inline-block",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    transform: isHovered ? "scale(1.2)" : "scale(1)",
    cursor: "pointer",
    ...baseStyle,
  };
}

// =============== STAGEOVERVIEW ===============
function StageOverview({
  initiatives,
  stageChoices,
  unit = "",
  showUnitBefore = false,
  onInitiativeClick,
  initiativeDetailsOpen = false,
}) {
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#2196f3";
  const clientColor2 = userDetails?.implementation_color2 || "#2196f3";
  const [hoveredItem, setHoveredItem] = useState(null); // hover de QUALQUER nível
  const [loadingInitId, setLoadingInitId] = useState(null); // spinner interno

  const allStages = React.useMemo(() => {
    // Show all stages from stageChoices, even if no initiatives
    if (stageChoices && stageChoices.length > 0) {
      return stageChoices.map((choice) => choice.display);
    }
    // Fallback: get stages from initiatives if no stageChoices provided
    const stagesFromInitiatives = new Set(
      initiatives.map((init) => init.stage || "Draft")
    );
    return Array.from(stagesFromInitiatives);
  }, [initiatives, stageChoices]);

  // Montar estatísticas
  const stageStats = React.useMemo(() => {
    const stats = {};
    allStages.forEach((st) => {
      stats[st] = { count: 0, totalKpi: 0 };
    });
    initiatives.forEach((init) => {
      const st = init.stage || "Draft";
      stats[st].count++;
      stats[st].totalKpi += init.kpiValue || 0;
    });
    return stats;
  }, [allStages, initiatives]);

  // soma total de kpiValue
  const totalKpiAllStages = allStages.reduce(
    (acc, st) => acc + stageStats[st].totalKpi,
    0
  );

  const [stageDrawerOpen, setStageDrawerOpen] = React.useState(false);
  const [selectedStage, setSelectedStage] = React.useState(null);
  const [stageInits, setStageInits] = React.useState([]);

  React.useEffect(() => {
    if (initiativeDetailsOpen) {
      setStageDrawerOpen(false); // fecha ao abrir detalhe
    } else if (!initiativeDetailsOpen && selectedStage) {
      setStageDrawerOpen(true); // reabre ao fechar detalhe
    }
  }, [initiativeDetailsOpen, selectedStage]);

  const handleStageClick = (stage) => {
    // filtra as initiatives
    const inThisStage = initiatives.filter(
      (init) => (init.stage || "Draft") === stage
    );
    setSelectedStage(stage);
    setStageInits(inThisStage);
    setStageDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setStageDrawerOpen(false);
    setSelectedStage(null);
    setStageInits([]);
  };

  const handleInitClickWrapper = async (id) => {
    setLoadingInitId(id); // exibe spinner
    if (onInitiativeClick) {
      // Se onInitiativeClick devolve uma Promise, esperamos
      const maybePromise = onInitiativeClick(id);
      if (maybePromise && typeof maybePromise.then === "function") {
        try {
          await maybePromise;
        } finally {
          setLoadingInitId(null); // desliga spinner quando finalizar
        }
      } else {
        // Se não for Promise, desligamos imediatamente
        setLoadingInitId(null);
      }
    } else {
      setLoadingInitId(null);
    }
  };

  const { r: baseR, g: baseG, b: baseB } = parseHexColor(clientColor);

  function getAlphaForStage(index, total) {
    if (total <= 1) return 1;
    const minAlpha = 0.4;
    const alphaRange = 1 - minAlpha;
    const step = alphaRange / (total - 1);
    return minAlpha + index * step;
  }

  // Calcular o total de iniciativas
  const totalInitiatives = initiatives.length;

  return (
    <div style={{ padding: "20px 16px", marginTop: "-10px" }}>
      {/* Header com Total de Iniciativas */}
      <Box
        mb={-4}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Typography
          variant="h4"
          style={{
            fontWeight: 700,
            color: "#333",
            fontSize: "2rem",
            marginRight: "8px",
          }}
        >
          {totalInitiatives}
        </Typography>
        <Typography
          variant="subtitle1"
          style={{
            fontWeight: 500,
            color: "#666",
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          {t("whatshappening.table.total_initiatives") || "TOTAL DE INICIATIVAS"}
        </Typography>
      </Box>

      {/* Funil Horizontal */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        style={{ 
          minHeight: "180px",
          padding: "20px 0",
        }}
      >
        <svg
          width="100%"
          height="180"
          viewBox="0 0 1000 180"
          preserveAspectRatio="xMidYMid meet"
          style={{ maxWidth: "900px" }}
        >
          <defs>
            {/* Gradiente para cada estágio */}
            {allStages.map((stage, idx) => {
              const alpha = getAlphaForStage(idx, allStages.length);
              const color1 = `rgba(${baseR}, ${baseG}, ${baseB}, ${alpha})`;
              const color2 = `rgba(${baseR}, ${baseG}, ${baseB}, ${Math.min(alpha + 0.15, 1)})`;
              return (
                <linearGradient
                  key={`gradient-${stage}`}
                  id={`funnel-gradient-${idx}`}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={color1} stopOpacity="1" />
                  <stop offset="100%" stopColor={color2} stopOpacity="1" />
                </linearGradient>
              );
            })}
          </defs>

          {allStages.map((stage, idx) => {
            const { count, totalKpi } = stageStats[stage];
            const isClickable = count > 0; // Allow click if there are initiatives, regardless of KPI value
            const isHovered = hoveredItem === stage;
            
            // Calcular percentual do total
            const percentage = totalKpiAllStages > 0 
              ? ((totalKpi / totalKpiAllStages) * 100).toFixed(1)
              : "0.0";
            
            // Calcular a largura de cada seção do funil
            const sectionWidth = 1000 / allStages.length;
            const x = idx * sectionWidth;
            
            // Criar efeito de funil: altura diminui progressivamente
            const minHeight = 35;
            const maxHeight = 200;
            const totalStages = allStages.length;
            
            // Calcular altura do lado esquerdo (atual estágio)
            const leftHeightRatio = 1 - (idx / totalStages);
            const leftHeight = minHeight + (maxHeight - minHeight) * leftHeightRatio;
            
            // Calcular altura do lado direito (próximo estágio)
            const rightHeightRatio = 1 - ((idx + 1) / totalStages);
            const rightHeight = minHeight + (maxHeight - minHeight) * rightHeightRatio;
            
            const centerY = 90;
            const topLeftY = centerY - leftHeight / 2;
            const bottomLeftY = centerY + leftHeight / 2;
            const topRightY = centerY - rightHeight / 2;
            const bottomRightY = centerY + rightHeight / 2;
            
            // Pontos do trapézio
            const points = `${x},${topLeftY} ${x + sectionWidth},${topRightY} ${x + sectionWidth},${bottomRightY} ${x},${bottomLeftY}`;
            
            return (
              <g key={stage}>
                {/* Trapézio (seção do funil) */}
                <Tippy
                  content={`${stage}: ${count} ${t("whatshappening.table.initiatives_label") || "iniciativas"} | ${formatNumber(totalKpi, unit, showUnitBefore)}`}
                  theme="light"
                  arrow={true}
                >
                  <polygon
                    points={points}
                    fill={`url(#funnel-gradient-${idx})`}
                    stroke="white"
                    strokeWidth={isHovered ? "3" : "2"}
                    style={{
                      cursor: isClickable ? "pointer" : "default",
                      transition: "all 0.3s ease",
                      filter: isHovered ? `drop-shadow(0 4px 8px rgba(${baseR}, ${baseG}, ${baseB}, 0.4))` : "none",
                      opacity: isHovered ? 1 : 0.95,
                    }}
                    onClick={() => isClickable && handleStageClick(stage)}
                    onMouseEnter={(e) => {
                      setHoveredItem(stage);
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      setHoveredItem(null);
                      e.currentTarget.style.opacity = "0.95";
                    }}
                  />
                </Tippy>
                
                {/* Label do estágio */}
                <text
                  x={x + sectionWidth / 2}
                  y={centerY - leftHeight / 2 - 8}
                  textAnchor="middle"
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    fill: "#666",
                    pointerEvents: "none",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stage}
                </text>
                
                {/* Número de iniciativas */}
                <text
                  x={x + sectionWidth / 2}
                  y={centerY - 5}
                  textAnchor="middle"
                  style={{
                    fontSize: "28px",
                    fontWeight: 700,
                    fill: isHovered ? clientColor2 : "white",
                    pointerEvents: "none",
                    transition: "fill 0.3s ease",
                  }}
                >
                  {count}
                </text>
                
                {/* KPI */}
                <text
                  x={x + sectionWidth / 2}
                  y={centerY + 20}
                  textAnchor="middle"
                  style={{
                    fontSize: "15px",
                    fontWeight: 500,
                    fill: "white",
                    pointerEvents: "none",
                    opacity: 0.9,
                  }}
                >
                  {formatNumber(totalKpi, unit, showUnitBefore)} ({percentage}%)
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
      {/* Modern section for displaying Total */}
      <Box mt={-8} display="flex" justifyContent="flex-end">
        <Box
          style={{
            padding: "6px 14px",
            borderRadius: "6px",
            backgroundColor: "#fafafa",
            border: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Typography
            variant="body2"
            style={{ 
              fontSize: "0.8rem", 
              fontWeight: 500, 
              color: "#666" 
            }}
          >
            {t("whatshappening.table.total", "Total:")}
          </Typography>
          <Typography
            variant="body1"
            style={{ 
              fontSize: "0.9rem", 
              fontWeight: 600, 
              color: "#333" 
            }}
          >
            {formatNumber(totalKpiAllStages, unit, showUnitBefore)}
          </Typography>
        </Box>
      </Box>
      {/* Drawer com as iniciativas do stage */}
      <Drawer
        anchor="bottom"
        open={stageDrawerOpen}
        onClose={handleCloseDrawer}
        BackdropProps={{ style: { backgroundColor: "#000", opacity: "0.1" } }}
        PaperProps={{
          style: {
            borderRadius: "10px",
            height: "80vh",
            width: "1200px",
            padding: "10px",
            margin: "1vh auto", // centraliza horizontalmente
          },
        }}
      >
        <Typography variant="h6" style={{ marginBottom: "12px" }}>
          {t("whatshappening.table.initiatives_in_stage")}{" "}
          <span style={{ textTransform: "capitalize" }}>{selectedStage}</span>
        </Typography>
        <PerfectScrollbar
          style={{ width: "100%", height: "calc(100% - 48px)" }}
          options={{ suppressScrollX: false }}
        >
          {stageInits.length > 0 ? (
            <>
              {stageInits
                .slice()
                .sort((a, b) => b.kpiValue - a.kpiValue)
                .map((init) => {
                  const isHovered = hoveredItem === init.id; // ← compara id
                  const isLoading = loadingInitId === init.id; // ← spinner só aqui

                  return (
                    <div
                      key={init.id}
                      style={{
                        marginBottom: "10px",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: isHovered
                          ? getAlphaColor(clientColor, 1)
                          : "transparent",
                      }}
                      onMouseEnter={() => setHoveredItem(init.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={() => handleInitClickWrapper(init.id)}
                    >
                      {isLoading ? (
                        <>
                          <ThreeDots
                            color={userDetails.implementation_color}
                            height={20}
                            width={40}
                            ariaLabel="loading"
                          />
                          <Typography
                            variant="body2"
                            style={{
                              marginLeft: 8,
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            {t("whatshappening.messages.loading_details")}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <div style={{ flexGrow: 1 }}>
                            <Typography style={{ fontWeight: 600 }}>
                              {init.initiativeName}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ color: "#666" }}
                            >
                              {t("whatshappening.labels.branch")} {init.branch}{" "}
                              | {t("whatshappening.labels.workfront")}{" "}
                              {init.workfront} |{" "}
                              {t("whatshappening.table.kpi_drawer")}
                              {formatNumber(
                                init.kpiValue,
                                unit,
                                showUnitBefore
                              )}
                            </Typography>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              <Box
                style={{
                  borderTop: "1px solid #ccc",
                  paddingTop: "8px",
                  marginTop: "16px",
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  {t("whatshappening.table.total_kpi")}
                  {formatNumber(
                    stageInits.reduce((sum, i) => sum + (i.kpiValue || 0), 0),
                    unit,
                    showUnitBefore
                  )}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" style={{ color: "#aaa" }}>
              {t("whatshappening.table.no_initiatives")}
            </Typography>
          )}
        </PerfectScrollbar>
      </Drawer>
    </div>
  );
}
StageOverview.propTypes = {
  initiatives: PropTypes.array.isRequired,
  stageChoices: PropTypes.array,
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
  initiativeDetailsOpen: PropTypes.bool,
};

// =============== DRILLDOWN VIEW ===============

/**
 * buildNestedStructure(kpiOverviewData)
 * => array de branches, cada branch tem array de workfronts, cada workfront tem array de iniciativas.
 * Ordenado desc pelo totalKpi.
 * IMPORTANTE: Cada iniciativa deve aparecer apenas UMA VEZ nos dados (identificada por id único)
 */
function buildNestedStructure(kpiOverviewData) {
  const mapBranches = {};
  const processedInitiatives = new Set(); // Track para evitar duplicações

  for (const item of kpiOverviewData) {
    const {
      id,
      branch = "No Branch",
      workfront = "No Workfront",
      initiativeName = "No Initiative",
      kpiValue = 0,
    } = item;

    const initiativeKey = id || initiativeName;
    
    // Skip se já processamos esta iniciativa
    if (processedInitiatives.has(initiativeKey)) {
      continue;
    }
    processedInitiatives.add(initiativeKey);

    if (!mapBranches[branch]) {
      mapBranches[branch] = { totalKpi: 0, workfronts: {} };
    }
    mapBranches[branch].totalKpi += kpiValue;

    if (!mapBranches[branch].workfronts[workfront]) {
      mapBranches[branch].workfronts[workfront] = {
        totalKpi: 0,
        initiatives: {},
      };
    }
    mapBranches[branch].workfronts[workfront].totalKpi += kpiValue;

    // Usando o id como chave para garantir unicidade
    mapBranches[branch].workfronts[workfront].initiatives[initiativeKey] = {
      totalKpi: kpiValue, // Usar diretamente o valor, não somar
      id: initiativeKey,
      name: initiativeName, // Guardar o nome para exibição
    };
  }

  // Converte map -> array
  const branchesArray = [];
  for (const bName of Object.keys(mapBranches)) {
    const { totalKpi, workfronts } = mapBranches[bName];
    const wfArray = [];
    for (const wfName of Object.keys(workfronts)) {
      const wfData = workfronts[wfName];
      const initArray = [];
      for (const iName of Object.keys(wfData.initiatives)) {
        const { totalKpi, id, name } = wfData.initiatives[iName]; // totalKpi, id e name
        initArray.push({
          id, // <-- inclui o id real
          initiative: name, // Usar o nome da iniciativa, não a chave (UUID)
          totalKpi,
        });
      }
      // ordena iniciativas desc
      initArray.sort((a, b) => b.totalKpi - a.totalKpi);

      wfArray.push({
        workfront: wfName,
        totalKpi: wfData.totalKpi,
        initiatives: initArray,
      });
    }
    // ordena workfront desc
    wfArray.sort((a, b) => b.totalKpi - a.totalKpi);

    branchesArray.push({
      branch: bName,
      totalKpi,
      workfronts: wfArray,
    });
  }
  // ordena branch desc
  branchesArray.sort((a, b) => b.totalKpi - a.totalKpi);

  return branchesArray;
}

/**
 * SavingDrilldownView
 *  - "viewMode" => "branch" | "workfront" | "initiative"
 *  - arrows for up/down => global switch
 *  - manual drilldown inside "branch" mode by clicking items
 */
function SavingDrilldownView({
  kpiOverviewData = [],
  unit = "",
  showUnitBefore = false,
  clientColor = "#2196f3",
  onInitiativeClick,
}) {
  const { t } = useTranslation();
  // 3 modos globais
  const [viewMode, setViewMode] = useState("branch");

  // Seleção manual (apenas no modo "branch")
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedWorkfront, setSelectedWorkfront] = useState(null);

  // Montar a hierarquia
  const branchesData = useMemo(
    () => buildNestedStructure(kpiOverviewData),
    [kpiOverviewData]
  );

  // total geral
  const totalAcrossAll = useMemo(() => {
    let sum = 0;
    for (const br of branchesData) sum += br.totalKpi;
    return sum;
  }, [branchesData]);

  // allWorkfronts (para modo "workfront")
  const allWorkfronts = useMemo(() => {
    // ex: { label, totalKpi, branch, workfront }
    const arr = [];
    for (const br of branchesData) {
      for (const wf of br.workfronts) {
        arr.push({
          label: `${wf.workfront}`,
          totalKpi: wf.totalKpi,
          branch: br.branch,
          workfront: wf.workfront,
        });
      }
    }
    arr.sort((a, b) => b.totalKpi - a.totalKpi);
    return arr;
  }, [branchesData]);

  // allInitiatives (para modo "initiative")
  const allInitiatives = useMemo(() => {
    const arr = [];
    for (const br of branchesData) {
      for (const wf of br.workfronts) {
        for (const init of wf.initiatives) {
          arr.push({
            id: init.id, // garante que o id exista
            label: `${init.initiative}`,
            totalKpi: init.totalKpi,
            branch: br.branch,
            workfront: wf.workfront,
          });
        }
      }
    }
    arr.sort((a, b) => b.totalKpi - a.totalKpi);
    return arr;
  }, [branchesData]);

  // qual branch + workfront está selecionado
  const selectedBranchObj = selectedBranch
    ? branchesData.find((b) => b.branch === selectedBranch)
    : null;
  const selectedWorkfrontObj = useMemo(() => {
    if (!selectedBranchObj || !selectedWorkfront) return null;
    return selectedBranchObj.workfronts.find(
      (wf) => wf.workfront === selectedWorkfront
    );
  }, [selectedBranchObj, selectedWorkfront]);

  // mouse‑over e spinner no item de iniciativa
  const [hoveredItem, setHoveredItem] = useState(null);
  const [loadingInitId, setLoadingInitId] = useState(null);

  // Limpar estados ao mudar de nível para evitar renderizações fantasmas
  React.useEffect(() => {
    setHoveredItem(null);
    setLoadingInitId(null);
  }, [viewMode, selectedBranch, selectedWorkfront]);

  // =============== BOTÕES DE SETA ==================
  // arrowDown => "desce" um nível global, se possível
  const handleArrowDown = () => {
    if (viewMode === "branch") {
      // se está no branch, passamos p/ "workfront"
      setViewMode("workfront");
      // reset manual selection
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else if (viewMode === "workfront") {
      // passamos p/ "initiative"
      setViewMode("initiative");
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else {
      // se já está em initiative, não dá p/ descer mais => ignore ou desabilite
    }
  };

  // arrowUp => "sobe" um nível global, se possível
  // se estamos em modo "branch", podemos estar no drilldown manual
  // subimos do selectedWorkfront -> sem selectedWorkfront, ou se não tem, subimos da selectedBranch -> sem selectedBranch
  // se no final, não tem nada selecionado, não tem p/ onde subir => ou se no "branch" top, disable
  // se no "workfront"/"initiative", subimos p/ a anterior
  const handleArrowUp = () => {
    if (viewMode === "initiative") {
      setViewMode("workfront");
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else if (viewMode === "workfront") {
      setViewMode("branch");
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else {
      // = branch => check se tem workfront selecionado, se sim desmarcar
      if (selectedWorkfront) {
        setSelectedWorkfront(null);
      } else if (selectedBranch) {
        setSelectedBranch(null);
      } else {
        // já está no top-level do branch => não tem p/ onde subir => ignore ou desabilite
      }
    }
  };

  // =============== DRILLDOWN MANUAL (cliques) ===============
  const handleBranchClick = (branchItem) => {
    if (viewMode !== "branch") return; // only if in "branch" mode
    setSelectedBranch(branchItem.branch);
    setSelectedWorkfront(null);
  };

  const handleWorkfrontClick = (wfItem) => {
    setSelectedWorkfront(wfItem.workfront);
  };

  // Para "All Workfronts": clique e ir direto p/ ver iniciativas
  const handleAllWorkfrontClick = (wfItem) => {
    // mudar p/ "branch" e setar a branch e workfront
    setViewMode("branch");
    setSelectedBranch(wfItem.branch);
    setSelectedWorkfront(wfItem.workfront);
  };

  const handleBackFromBranch = () => {
    setSelectedBranch(null);
    setSelectedWorkfront(null);
  };

  const handleBackFromWorkfront = () => {
    setSelectedWorkfront(null);
  };

  const handleInitClickWrapper = async (initId) => {
    setLoadingInitId(initId); // mostra spinner
    if (onInitiativeClick) {
      const maybePromise = onInitiativeClick(initId);
      if (maybePromise && typeof maybePromise.then === "function") {
        try {
          await maybePromise; // espera carregar
        } finally {
          setLoadingInitId(null); // some spinner
        }
      } else {
        setLoadingInitId(null);
      }
    } else {
      setLoadingInitId(null);
    }
  };

  // =============== STYLES ===============
  const useStyles = makeStyles(() => ({
    container: {
      padding: "12px 16px",
      overflowX: "auto",
      position: "relative",
    },
    headerSection: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 16,
      paddingBottom: 8,
      borderBottom: "1px solid #e0e0e0",
    },
    breadcrumbWrapper: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.75rem",
      color: "#666",
      fontWeight: 500,
    },
    breadcrumbItem: {
      cursor: "pointer",
      transition: "color 0.2s",
      "&:hover": {
        color: clientColor || "#2196f3",
        textDecoration: "underline",
      },
    },
    breadcrumbActive: {
      color: clientColor || "#2196f3",
      fontWeight: 600,
    },
    navigationButtons: {
      display: "flex",
      gap: 6,
    },
    barRow: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      padding: "6px 0",
      cursor: "pointer",
      transition: "background-color 0.2s",
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.02)",
      },
    },
    barLabel: {
      width: 180,
      marginRight: 12,
      fontWeight: 500,
      fontSize: "0.75rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      transition: "color 0.2s",
    },
    bar: {
      height: "24px",
      borderRadius: 4,
      transition: "all 0.3s ease-in-out",
      backgroundColor: "#ccc",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      paddingRight: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      "&:hover": {
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        transform: "scaleY(1.1)",
      },
    },
    barValue: {
      marginLeft: 12,
      minWidth: 80,
      textAlign: "right",
      fontWeight: 600,
      fontSize: "0.75rem",
      whiteSpace: "nowrap",
      color: "#333",
    },
    totalCard: {
      marginTop: 12,
      padding: "8px 16px",
      borderRadius: 8,
      background: "linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)",
      border: "1px solid #e0e0e0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    cardButton: {
      padding: "4px !important",
      minWidth: "28px !important",
      height: "28px !important",
      borderRadius: "4px !important",
    },
    cardButtonIcon: {
      fontSize: "14px !important",
    },
  }));
  const classes = useStyles();

  const colorObj = parseHexColor(clientColor);

  const r = colorObj.r;
  const g = colorObj.g;
  const b = colorObj.b;
  const barBgColor = `rgba(${r}, ${g}, ${b}, 0.4)`;

  // Função auxiliar para renderizar lista de barras moderna
  function renderBarList(dataArray, totalOfAll, onItemClick) {
    const MAX_WIDTH = 500;
    return dataArray.map((item) => {
      const fraction = totalOfAll === 0 ? 0 : item.totalKpi / totalOfAll;
      const barWidth = Math.max(fraction * MAX_WIDTH, 20); // Mínimo de 20px
      const isHovered = hoveredItem === item.label;
      
      return (
        <div
          key={item.label}
          className={classes.barRow}
          onClick={() => onItemClick && onItemClick(item)}
          onMouseEnter={() => setHoveredItem(item.label)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <Tippy content={item.label} theme="light">
            <div 
              className={classes.barLabel}
              style={{ color: isHovered ? clientColor : "#333" }}
            >
              {item.label}
            </div>
          </Tippy>
          <div
            className={classes.bar}
            style={{ 
              width: `${barWidth}px`, 
              backgroundColor: barBgColor,
              border: isHovered ? `1px solid ${clientColor}` : "1px solid transparent"
            }}
          />
          <div className={classes.barValue}>
            {formatNumber(item.totalKpi, unit, showUnitBefore)}
          </div>
        </div>
      );
    });
  }

  // =============== RENDER HELPERS ===============
  // Breadcrumb para navegação
  const renderBreadcrumb = () => {
    const items = [];
    
    if (viewMode === "branch") {
      items.push(
        <span key="all" className={!selectedBranch ? classes.breadcrumbActive : classes.breadcrumbItem} onClick={() => {
          setSelectedBranch(null);
          setSelectedWorkfront(null);
        }}>
          {t("whatshappening.labels.all_branches", "Todas")}
        </span>
      );
      
      if (selectedBranch) {
        items.push(<span key="sep1" style={{ color: "#ccc" }}>›</span>);
        items.push(
          <span key="branch" className={!selectedWorkfront ? classes.breadcrumbActive : classes.breadcrumbItem} onClick={handleBackFromWorkfront}>
            {selectedBranch}
          </span>
        );
      }
      
      if (selectedWorkfront) {
        items.push(<span key="sep2" style={{ color: "#ccc" }}>›</span>);
        items.push(
          <span key="wf" className={classes.breadcrumbActive}>
            {selectedWorkfront}
          </span>
        );
      }
    } else if (viewMode === "workfront") {
      items.push(
        <span key="all-wf" className={classes.breadcrumbActive}>
          {t("whatshappening.labels.all_workfronts", "Workfronts")}
        </span>
      );
    } else {
      items.push(
        <span key="all-init" className={classes.breadcrumbActive}>
          {t("whatshappening.labels.all_initiatives", "Iniciativas")}
        </span>
      );
    }
    
    return <div className={classes.breadcrumbWrapper}>{items}</div>;
  };

  // =============== RENDER ===============
  // Key única para forçar remontagem ao mudar de nível
  const contentKey = `${viewMode}-${selectedBranch || 'none'}-${selectedWorkfront || 'none'}`;
  
  return (
    <div className={classes.container}>
      {/* Header com Breadcrumb e Navegação */}
      <div className={classes.headerSection}>
        {renderBreadcrumb()}
        
        <div className={classes.navigationButtons}>
          {/* Botão de Subir */}
          <Tippy content={t("whatshappening.buttons.up_hierarchy")} theme="light">
            <Button
              color="client"
              size="sm"
              variant="outlined"
              onClick={handleArrowUp}
              className={classes.cardButton}
            >
              <FontAwesomeIcon icon={faArrowUp} className={classes.cardButtonIcon} />
            </Button>
          </Tippy>

          {/* Botão de Descer */}
          <Tippy content={t("whatshappening.buttons.down_hierarchy")} theme="light">
            <Button
              color="client"
              size="sm"
              variant="outlined"
              onClick={handleArrowDown}
              className={classes.cardButton}
            >
              <FontAwesomeIcon icon={faArrowDown} className={classes.cardButtonIcon} />
            </Button>
          </Tippy>
        </div>
      </div>

      {/* Área de conteúdo com key para forçar remontagem limpa */}
      <div key={contentKey}>
        {viewMode === "branch" ? (
        // ==================== MODO "BRANCH" + DRILLDOWN MANUAL ====================
        <>
          {!selectedBranch ? (
            // (1) Top level: listar todas as Branches
            <>
              {branchesData.map((br) => {
                const frac = totalAcrossAll === 0 ? 0 : br.totalKpi / totalAcrossAll;
                const barWidth = Math.max(Math.round(frac * 500), 20);
                const isHovered = hoveredItem === br.branch;
                return (
                  <div
                    key={br.branch}
                    className={classes.barRow}
                    onClick={() => handleBranchClick(br)}
                    onMouseEnter={() => setHoveredItem(br.branch)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Tippy content={br.branch} theme="light">
                      <div
                        className={classes.barLabel}
                        style={{ color: isHovered ? clientColor : "#333" }}
                      >
                        {br.branch}
                      </div>
                    </Tippy>
                    <div
                      className={classes.bar}
                      style={{
                        width: `${barWidth}px`,
                        backgroundColor: barBgColor,
                        border: isHovered ? `1px solid ${clientColor}` : "1px solid transparent"
                      }}
                    />
                    <div className={classes.barValue}>
                      {formatNumber(br.totalKpi, unit, showUnitBefore)}
                    </div>
                  </div>
                );
              })}
              <div className={classes.totalCard}>
                <Typography variant="body2" style={{ fontWeight: 500, color: "#666", fontSize: "0.7rem" }}>
                  {t("whatshappening.table.total", "Total:")}
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 700, color: "#000", fontSize: "0.9rem" }}>
                  {formatNumber(totalAcrossAll, unit, showUnitBefore)}
                </Typography>
              </div>
            </>
          ) : !selectedWorkfront ? (
            // (2) Branch selecionada, listar workfronts
            <>
              {selectedBranchObj?.workfronts.map((wf) => {
                const frac = selectedBranchObj?.totalKpi
                  ? wf.totalKpi / selectedBranchObj.totalKpi
                  : 0;
                const barWidth = Math.max(Math.round(frac * 500), 20);
                const isHovered = hoveredItem === wf.workfront;
                return (
                  <div
                    key={wf.workfront}
                    className={classes.barRow}
                    onClick={() => handleWorkfrontClick(wf)}
                    onMouseEnter={() => setHoveredItem(wf.workfront)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Tippy content={wf.workfront} theme="light">
                      <div
                        className={classes.barLabel}
                        style={{ color: isHovered ? clientColor : "#333" }}
                      >
                        {wf.workfront}
                      </div>
                    </Tippy>
                    <div
                      className={classes.bar}
                      style={{
                        width: `${barWidth}px`,
                        backgroundColor: barBgColor,
                        border: isHovered ? `1px solid ${clientColor}` : "1px solid transparent"
                      }}
                    />
                    <div className={classes.barValue}>
                      {formatNumber(wf.totalKpi, unit, showUnitBefore)}
                    </div>
                  </div>
                );
              })}
              <div className={classes.totalCard}>
                <Typography variant="body2" style={{ fontWeight: 500, color: "#666", fontSize: "0.7rem" }}>
                  {t("whatshappening.table.total", "Total:")}
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 700, color: "#000", fontSize: "0.9rem" }}>
                  {formatNumber(selectedBranchObj?.totalKpi, unit, showUnitBefore)}
                </Typography>
              </div>
            </>
          ) : (
            // (3) Workfront selecionado => listar iniciativas
            <>
              {selectedWorkfrontObj?.initiatives.map((init) => {
                const frac = selectedWorkfrontObj?.totalKpi
                  ? init.totalKpi / selectedWorkfrontObj.totalKpi
                  : 0;
                const barWidth = Math.max(Math.round(frac * 500), 20);
                const isHovered = hoveredItem === init.id;
                const isLoading = loadingInitId === init.id;

                return (
                  <div
                    key={init.initiative}
                    className={classes.barRow}
                    onMouseEnter={() => setHoveredItem(init.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => handleInitClickWrapper(init.id)}
                  >
                    {isLoading ? (
                      <>
                        <ThreeDots
                          color={clientColor}
                          height={20}
                          width={40}
                          ariaLabel="loading"
                        />
                        <Typography
                          variant="body2"
                          style={{
                            marginLeft: 8,
                            fontStyle: "italic",
                            color: "#666",
                            fontSize: "0.7rem"
                          }}
                        >
                          {t("whatshappening.messages.loading_details")}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Tippy content={init.initiative} theme="light">
                          <div
                            className={classes.barLabel}
                            style={{ color: isHovered ? clientColor : "#333" }}
                          >
                            {init.initiative}
                          </div>
                        </Tippy>
                        <div
                          className={classes.bar}
                          style={{
                            width: `${barWidth}px`,
                            backgroundColor: barBgColor,
                            border: isHovered ? `1px solid ${clientColor}` : "1px solid transparent"
                          }}
                        />
                        <div className={classes.barValue}>
                          {formatNumber(init.totalKpi, unit, showUnitBefore)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              <div className={classes.totalCard}>
                <Typography variant="body2" style={{ fontWeight: 500, color: "#666", fontSize: "0.7rem" }}>
                  {t("whatshappening.table.total", "Total:")}
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 700, color: "#000", fontSize: "0.9rem" }}>
                  {formatNumber(selectedWorkfrontObj?.totalKpi, unit, showUnitBefore)}
                </Typography>
              </div>
            </>
          )}
        </>
      ) : viewMode === "workfront" ? (
        // ==================== ALL WORKFRONTS (DESC) ====================
        <>
          {renderBarList(allWorkfronts, totalAcrossAll, (wfItem) =>
            handleAllWorkfrontClick(wfItem)
          )}
          <div className={classes.totalCard}>
            <Typography variant="body2" style={{ fontWeight: 500, color: "#666", fontSize: "0.7rem" }}>
              {t("whatshappening.table.total", "Total:")}
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 700, color: "#000", fontSize: "0.9rem" }}>
              {formatNumber(totalAcrossAll, unit, showUnitBefore)}
            </Typography>
          </div>
        </>
      ) : (
        // ==================== ALL INITIATIVES (DESC) ====================
        <>
          {allInitiatives.map((init) => {
            const frac = totalAcrossAll === 0 ? 0 : init.totalKpi / totalAcrossAll;
            const barWidth = Math.max(Math.round(frac * 500), 20);
            const isHovered = hoveredItem === init.id;
            const isLoading = loadingInitId === init.id;

            return (
              <div
                key={init.id}
                className={classes.barRow}
                onMouseEnter={() => setHoveredItem(init.id)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => handleInitClickWrapper(init.id)}
              >
                {isLoading ? (
                  <>
                    <ThreeDots
                      color={clientColor}
                      height={20}
                      width={40}
                      ariaLabel="loading"
                    />
                    <Typography
                      variant="body2"
                      style={{
                        marginLeft: 8,
                        fontStyle: "italic",
                        color: "#666",
                        fontSize: "0.7rem"
                      }}
                    >
                      {t("whatshappening.messages.loading_details")}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Tippy content={init.label} theme="light">
                      <div 
                        className={classes.barLabel}
                        style={{ color: isHovered ? clientColor : "#333" }}
                      >
                        {init.label}
                      </div>
                    </Tippy>
                    <div
                      className={classes.bar}
                      style={{
                        width: `${barWidth}px`,
                        backgroundColor: barBgColor,
                        border: isHovered ? `1px solid ${clientColor}` : "1px solid transparent"
                      }}
                    />
                    <div className={classes.barValue}>
                      {formatNumber(init.totalKpi, unit, showUnitBefore)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className={classes.totalCard}>
            <Typography variant="body2" style={{ fontWeight: 500, color: "#666", fontSize: "0.7rem" }}>
              {t("whatshappening.table.total", "Total:")}
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 700, color: "#000", fontSize: "0.9rem" }}>
              {formatNumber(totalAcrossAll, unit, showUnitBefore)}
            </Typography>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

// =============== ROW COMPONENT ===============
function StatusRow({
  row,
  level = 0,
  unit = "",
  showUnitBefore = false,
  clientColor,
  classes,
  hoveredInitId,
  setHoveredInitId,
  loadingInitId,
  handleInitClickWrapper,
}) {
  const [open, setOpen] = useState(false);
  const isInitiative = !row.children || row.children.length === 0; // ↙ novo
  const isHovered = isInitiative && hoveredInitId === row.id;
  const isLoading = isInitiative && loadingInitId === row.id;
  const { t } = useTranslation();

  return (
    <>
      <TableRow
        className={level === 0 ? classes.compactRow : classes.childRow}
        style={{
          backgroundColor: level > 0 ? "#fafafa" : "transparent",
          cursor: isInitiative ? "pointer" : "default",
        }}
        onClick={() => isInitiative && handleInitClickWrapper(row.id)}
      >
        {/* Expand Icon */}
        <TableCell className={classes.tableCell} style={{ width: "40px", minWidth: "40px", maxWidth: "40px", padding: "4px 8px" }}>
          {row.children && row.children.length > 0 && (
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              style={{ padding: "2px" }}
            >
              {open ? (
                <KeyboardArrowUpIcon style={{ fontSize: "18px" }} />
              ) : (
                <KeyboardArrowDownIcon style={{ fontSize: "18px" }} />
              )}
            </IconButton>
          )}
        </TableCell>
        {/* Name Cell - sem indentação para manter alinhamento */}
        <TableCell
          className={`${classes.tableCell} ${classes.ellipsisCell}`}
          onMouseEnter={() => isInitiative && setHoveredInitId(row.id)}
          onMouseLeave={() => isInitiative && setHoveredInitId(null)}
        >
          {isLoading ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <ThreeDots
                color={clientColor}
                height={20}
                width={40}
                ariaLabel="loading"
              />
              <Typography
                variant="body2"
                style={{ marginLeft: 8, fontStyle: "italic", color: "#666" }}
              >
                {t("whatshappening.messages.loading_details")}
              </Typography>
            </div>
          ) : (
            <Tippy content={row.name} theme="light">
              <span
                style={{
                  display: "inline-block",
                  width: "100%",
                  color: isHovered ? clientColor : "inherit",
                }}
              >
                {row.name}
              </span>
            </Tippy>
          )}
        </TableCell>

        {/* Líder - só mostra valor na dimensão iniciativa */}
        <TableCell 
          className={`${classes.tableCell} ${classes.ellipsisCell}`} 
          style={{ width: "120px", minWidth: "120px", maxWidth: "120px" }}
        >
          {isInitiative ? (
            <Tippy content={row.leader || ""} theme="light" disabled={!row.leader}>
              <span style={{ fontSize: "0.75rem", color: "#555" }}>
                {row.leader || "-"}
              </span>
            </Tippy>
          ) : (
            <span style={{ color: "#ccc" }}>-</span>
          )}
        </TableCell>

        {/* Number of initiatives */}
        <TableCell align="center" className={classes.tableCell} style={{ width: "80px", minWidth: "80px", maxWidth: "80px", fontWeight: 500 }}>
          {formatNumber(row.initiatives)}
        </TableCell>

        <TableCell align="right" className={classes.tableCell} style={{ width: "100px", minWidth: "100px", maxWidth: "100px", fontWeight: 500 }}>
          {formatNumber(row.kpiValue, unit, showUnitBefore)}
        </TableCell>

        {/* Status circle */}
        <TableCell align="center" className={classes.tableCell} style={{ width: "70px", minWidth: "70px", maxWidth: "70px" }}>
          <span
            className={`${classes.hoverCell} ${
              isHovered && !isLoading ? "filling" : ""
            }`}
            onMouseEnter={() => isInitiative && setHoveredInitId(row.id)}
            onMouseLeave={() => isInitiative && setHoveredInitId(null)}
            style={getStatusStyles(row.status, isHovered)}
          />
        </TableCell>
      </TableRow>

      {row.children && row.children.length > 0 && (
        <TableRow>
          <TableCell
            colSpan={6}
            style={{ paddingBottom: 0, paddingTop: 0, borderBottom: "none" }}
            className={classes.tableCell}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box style={{ margin: 0 }}>
                <Table size="small" aria-label="child-rows" style={{ tableLayout: "fixed", width: "100%" }}>
                  <TableBody>
                    {row.children.map((child) => (
                      <StatusRow
                        key={child.name}
                        row={child}
                        level={level + 1}
                        unit={unit}
                        showUnitBefore={showUnitBefore}
                        clientColor={clientColor}
                        classes={classes}
                        hoveredInitId={hoveredInitId}
                        setHoveredInitId={setHoveredInitId}
                        loadingInitId={loadingInitId}
                        handleInitClickWrapper={handleInitClickWrapper}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
StatusRow.propTypes = {
  row: PropTypes.shape({
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    kpiValue: PropTypes.number.isRequired,
    initiatives: PropTypes.number.isRequired,
    leader: PropTypes.string,
    children: PropTypes.array,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  level: PropTypes.number,
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
};

// =============== MAIN COMPONENT ===============
export default function CollapsibleStatusTable(props) {
  const {
    viewModeOverview,
    kpiOverviewData,
    stageChoices,
    selectedSection,
    unit = "",
    showUnitBefore = false,
    onInitiativeClick,
    initiativeDetailsOpen = false,
  } = props;

  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#2196f3";
  const { t } = useTranslation();

  // =========== STYLES
  const useDynamicStyles = (clientColor) => ({
    // ====== Estilos para o efeito "fill" quando passa o mouse no círculo de status ======
    hoverCell: {
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
      "&::before": {
        content: "''",
        position: "absolute",
        top: 0,
        left: 0,
        width: "0%",
        height: "100%",
        backgroundColor: clientColor
          ? `${clientColor}1A`
          : "rgba(33, 150, 243, 0.05)",
        transition: "width 1s linear",
      },
      "&.filling::before": {
        animation: "$fillCell 1s linear forwards",
      },
    },

    "@keyframes fillCell": {
      "0%": { width: "0%" },
      "100%": { width: "100%" },
    },

    // ====== Ícones de sort ======
    sortIcon: {
      fontSize: "11px",
      marginLeft: "4px",
      color: "#ccc",
      transition: "color 0.2s",
    },
    activeSortIcon: {
      color: clientColor || "#2196f3",
    },

    // ====== Table Cells - Compactas e Modernas ======
    tableCell: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "middle",
      lineHeight: 1.2,
      padding: "6px 8px", // Muito mais compacto
      fontSize: "0.75rem", // Fonte menor
      borderBottom: "1px solid #f0f0f0", // Borda sutil
    },
    
    tableHeaderCell: {
      padding: "8px 8px",
      fontSize: "0.7rem",
      fontWeight: 600,
      color: "#666",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      borderBottom: "2px solid #e0e0e0",
      backgroundColor: "#fafafa",
    },
    
    ellipsisCell: {
      maxWidth: 200,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "middle",
    },
    
    compactRow: {
      height: "32px", // Altura fixa compacta
      transition: "background-color 0.2s",
      "&:hover": {
        backgroundColor: "#f9f9f9",
      },
    },
    
    childRow: {
      backgroundColor: "#fafafa",
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  });

  const useStyles = makeStyles((theme) => useDynamicStyles(theme, clientColor));
  const classes = useStyles();

  // Precisamos converter data => buildHierarchyFromInitiatives
  // Lembrando que a view do back envia:
  // {
  //   id, branch, workfront, initiativeName,
  //   status, stage,
  //   kpiValue, kpiCapturedYTD, kpiTargetYTD
  // }
  // E o buildHierarchyFromInitiatives espera as mesmas props. Então OK:
  const hierarchyData = buildHierarchyFromInitiatives(
    props.kpiOverviewData || []
  );

  const [hoveredInitId, setHoveredInitId] = useState(null);
  const [loadingInitId, setLoadingInitId] = useState(null);

  const handleInitClickWrapper = async (id) => {
    setLoadingInitId(id); // ativa spinner
    if (onInitiativeClick) {
      const maybePromise = onInitiativeClick(id);
      if (maybePromise && typeof maybePromise.then === "function") {
        try {
          await maybePromise; // espera carregar
        } finally {
          setLoadingInitId(null); // desliga spinner
        }
      } else {
        setLoadingInitId(null);
      }
    } else {
      setLoadingInitId(null);
    }
  };

  // =========== SORTING
  const [sortConfig, setSortConfig] = useState({
    key: "status",
    direction: "ascending",
  });
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = sortDataRecursively(hierarchyData, sortConfig);

  return (
    <>
      {viewModeOverview === "table" ? (
        // ================= TABLE VIEW =================
        <TableContainer component={Paper} elevation={0} style={{ boxShadow: "none" }}>
          <Table style={{ tableLayout: "fixed", width: "100%" }} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell} style={{ width: "40px" }} />
                <TableCell
                  className={classes.tableHeaderCell}
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer" }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {t("whatshappening.table.name")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "name"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "name"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
                <TableCell 
                  className={classes.tableHeaderCell} 
                  style={{ width: "120px" }}
                >
                  {t("whatshappening.table.leader", "Líder")}
                </TableCell>
                <TableCell
                  className={classes.tableHeaderCell}
                  align="center"
                  onClick={() => handleSort("initiatives")}
                  style={{ cursor: "pointer", width: "80px" }}
                >
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {t("whatshappening.table.number_initiatives")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "initiatives"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "initiatives"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
                <TableCell
                  className={classes.tableHeaderCell}
                  align="right"
                  onClick={() => handleSort("kpiValue")}
                  style={{ cursor: "pointer", width: "100px" }}
                >
                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    {t("whatshappening.table.table_kpi")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "kpiValue"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "kpiValue"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
                <TableCell
                  className={classes.tableHeaderCell}
                  align="center"
                  onClick={() => handleSort("status")}
                  style={{ cursor: "pointer", width: "70px" }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {t("whatshappening.table.status")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "status"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "status"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((branch) => (
                <StatusRow
                  key={branch.name}
                  row={branch}
                  level={0}
                  unit={unit}
                  showUnitBefore={showUnitBefore}
                  clientColor={clientColor}
                  classes={classes}
                  hoveredInitId={hoveredInitId}
                  setHoveredInitId={setHoveredInitId}
                  loadingInitId={loadingInitId}
                  handleInitClickWrapper={handleInitClickWrapper}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : viewModeOverview === "stages" ? (
        // ================= STAGE OVERVIEW =================
        <StageOverview
          initiatives={kpiOverviewData || []}
          stageChoices={stageChoices}
          unit={unit}
          showUnitBefore={showUnitBefore}
          onInitiativeClick={onInitiativeClick}
          initiativeDetailsOpen={initiativeDetailsOpen}
        />
      ) : (
        <SavingDrilldownView
          kpiOverviewData={kpiOverviewData || []}
          unit={unit}
          showUnitBefore={showUnitBefore}
          clientColor={clientColor}
          onInitiativeClick={onInitiativeClick}
        />
      )}
    </>
  );
}

CollapsibleStatusTable.propTypes = {
  selectedSection: PropTypes.object,
  viewModeOverview: PropTypes.oneOf(["table", "stages"]).isRequired,
  kpiOverviewData: PropTypes.array.isRequired,
  stageChoices: PropTypes.array, // New prop for stage choices
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
  initiativeDetailsOpen: PropTypes.bool,
};
